/**
 * DashboardPage Component
 *
 * Main container page for the document dashboard.
 * Integrates all dashboard components: sidebar, search, view modes, and document lists.
 *
 * @module features/dashboard/pages/DashboardPage
 * @see SPEC-016 Requirements 3.1, 4.1, 5.1, 11.1
 */

import React, { useCallback, useState, useMemo } from 'react';
import { cn } from '../../../lib/cn';
import type { DocListItemInput, FolderNode } from '@linkode/shared';

// Components
import {
  FolderSidebar,
  SearchBar,
  ViewModeToggle,
  ListView,
  GridView,
  FlowView,
  EmptyState,
  QuickActionsMenu,
  type QuickAction,
  type MenuPosition,
  generateDuplicateTitle,
} from '../components';

// Hooks
import { useDashboard } from '../hooks/useDashboard';
import { useFolders } from '../hooks/useFolders';
import { useTypedTranslation } from '../../../i18n';

// ============================================================================
// Types
// ============================================================================

export interface DashboardPageProps {
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

/**
 * DashboardPage Component
 *
 * Main dashboard container that orchestrates:
 * - Folder sidebar navigation
 * - Search and filter toolbar
 * - View mode switching (List/Grid/Flow)
 * - Document list display
 * - Quick actions context menu
 * - Empty states and loading
 *
 * @example
 * ```tsx
 * // In App.tsx routes
 * <Route
 *   path="/dashboard"
 *   element={
 *     <AuthGuard>
 *       <DashboardPage />
 *     </AuthGuard>
 *   }
 * />
 * ```
 *
 * @see Requirements 3.1, 4.1, 5.1, 11.1
 */
export const DashboardPage: React.FC<DashboardPageProps> = ({ className }) => {
  const { t } = useTypedTranslation();

  // ─────────────────────────────────────────────────────────────────────────
  // Dashboard State
  // ─────────────────────────────────────────────────────────────────────────

  const {
    docs,
    total,
    hasMore,
    isLoading,
    error,
    viewMode,
    selectedDocIds,
    sortBy,
    sortOrder,
    searchQuery,
    hasHydrated,
    setViewMode,
    setSortBy,
    setSortOrder,
    setSearchQuery,
    toggleDocSelection,
    deleteDoc,
    moveDoc,
    loadMore,
    refresh,
    openDoc,
    createNewDoc,
    clearError,
    isCreatingDoc,
  } = useDashboard();

  // ─────────────────────────────────────────────────────────────────────────
  // Folder State
  // ─────────────────────────────────────────────────────────────────────────

  const {
    folderTree,
    selectedFolderId,
    isLoading: foldersLoading,
    selectFolder,
    toggleFolderExpansion,
    openCreateDialog,
  } = useFolders();

  // ─────────────────────────────────────────────────────────────────────────
  // Local UI State
  // ─────────────────────────────────────────────────────────────────────────

  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [contextMenuDoc, setContextMenuDoc] = useState<DocListItemInput | null>(null);
  const [contextMenuPosition, setContextMenuPosition] = useState<MenuPosition>({ x: 0, y: 0 });
  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Handlers
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Handle document context menu (right-click)
   */
  const handleDocContextMenu = useCallback((doc: DocListItemInput, event: React.MouseEvent) => {
    event.preventDefault();
    setContextMenuDoc(doc);
    setContextMenuPosition({ x: event.clientX, y: event.clientY });
    setIsContextMenuOpen(true);
  }, []);

  /**
   * Close context menu
   */
  const handleCloseContextMenu = useCallback(() => {
    setIsContextMenuOpen(false);
    setContextMenuDoc(null);
  }, []);

  /**
   * Handle quick action from context menu
   */
  const handleQuickAction = useCallback(
    async (action: QuickAction, doc: DocListItemInput) => {
      switch (action) {
        case 'rename':
          // TODO: Implement inline rename dialog
          break;

        case 'delete':
          if (window.confirm(t('dashboard.confirmDelete', { title: doc.title }))) {
            try {
              await deleteDoc(doc.docId);
            } catch {
              // Error is handled by the hook
            }
          }
          break;

        case 'duplicate': {
          // TODO: Implement duplicate via API
          void generateDuplicateTitle(doc.title);
          break;
        }

        case 'move':
          // TODO: Implement folder picker dialog
          break;
      }
    },
    [deleteDoc]
  );

  /**
   * Handle sort change from ListView
   */
  const handleSortChange = useCallback(
    (newSortBy: typeof sortBy, newSortOrder: typeof sortOrder) => {
      if (newSortBy !== sortBy) {
        setSortBy(newSortBy);
      }
      if (newSortOrder !== sortOrder) {
        setSortOrder(newSortOrder);
      }
    },
    [sortBy, sortOrder, setSortBy, setSortOrder]
  );

  /**
   * Handle folder context menu
   */
  const handleFolderContextMenu = useCallback((_folder: FolderNode, event: React.MouseEvent) => {
    event.preventDefault();
    // TODO: Implement folder context menu
  }, []);

  /**
   * Toggle mobile sidebar
   */
  const handleToggleMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Computed State
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Determine empty state type
   */
  const emptyStateType = useMemo(() => {
    if (error) return 'error';
    if (searchQuery && docs.length === 0) return 'no-results';
    if (selectedFolderId && docs.length === 0) return 'empty-folder';
    if (docs.length === 0) return 'no-documents';
    return null;
  }, [error, searchQuery, selectedFolderId, docs.length]);

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  // Show loading skeleton while hydrating
  if (!hasHydrated) {
    return (
      <div className="flex h-screen bg-linkode-bg">
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-linkode-text border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex h-screen pb-10 bg-linkode-bg', className)}>
      {/* Folder Sidebar - handles its own responsive behavior */}
      <FolderSidebar
        folderTree={folderTree}
        selectedFolderId={selectedFolderId}
        onFolderSelect={selectFolder}
        onFolderToggle={toggleFolderExpansion}
        onCreateFolder={openCreateDialog}
        onFolderContextMenu={handleFolderContextMenu}
        isLoading={foldersLoading}
        isMobileOpen={isMobileSidebarOpen}
        onMobileToggle={handleToggleMobileSidebar}
      />

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Toolbar */}
        <header className="flex items-center gap-4 px-6 py-4 border-b-2 border-linkode-text bg-white">
          {/* Mobile Menu Button - only visible on mobile when sidebar is closed */}
          <button
            type="button"
            className={cn(
              'p-2 rounded-linkode md:hidden',
              'text-linkode-text hover:bg-linkode-bg-light',
              'focus:outline-none focus:ring-2 focus:ring-linkode-teal'
            )}
            onClick={handleToggleMobileSidebar}
            aria-label={t('dashboard.toolbar.toggleSidebar')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* Search Bar */}
          <SearchBar
            value={searchQuery}
            onSearch={setSearchQuery}
            placeholder={t('dashboard.toolbar.searchPlaceholder')}
            fullWidth
            className="flex-1 max-w-md"
          />

          {/* Spacer */}
          <div className="flex-1" />

          {/* View Mode Toggle */}
          <ViewModeToggle value={viewMode} onChange={setViewMode} />

          {/* New Document Button */}
          <button
            type="button"
            onClick={createNewDoc}
            disabled={isCreatingDoc}
            className={cn(
              'inline-flex items-center gap-2',
              'px-4 py-2',
              'border-2 border-linkode-text rounded-linkode',
              'bg-linkode-primary text-linkode-text',
              'font-mono font-bold uppercase text-sm',
              'transition-all duration-200',
              'hover:shadow-linkode-hover hover:translate-x-0.5 hover:-translate-y-0.5',
              'focus:outline-none focus:ring-2 focus:ring-linkode-teal',
              'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:translate-x-0 disabled:hover:translate-y-0'
            )}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <span className="hidden sm:inline">{t('dashboard.toolbar.newDocument')}</span>
          </button>
        </header>

        {/* Document Count */}
        <div className="px-6 py-2 text-sm text-linkode-text/60 font-mono border-b border-linkode-text/10">
          {t('dashboard.documentCount_other', { count: total })}
          {searchQuery && ` ${t('dashboard.documentCountMatching', { query: searchQuery })}`}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-6">
          {/* Empty State */}
          {emptyStateType && (
            <EmptyState
              type={emptyStateType}
              actionLabel={
                emptyStateType === 'no-documents'
                  ? t('dashboard.emptyState.noDocuments.action')
                  : emptyStateType === 'error'
                    ? t('dashboard.emptyState.error.action')
                    : undefined
              }
              onAction={
                emptyStateType === 'no-documents'
                  ? createNewDoc
                  : emptyStateType === 'error'
                    ? () => {
                        clearError();
                        refresh();
                      }
                    : undefined
              }
            />
          )}

          {/* Document Views */}
          {!emptyStateType && (
            <>
              {viewMode === 'list' && (
                <ListView
                  docs={docs}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  selectedDocIds={selectedDocIds}
                  onSortChange={handleSortChange}
                  onDocClick={openDoc}
                  onDocContextMenu={handleDocContextMenu}
                  onDocSelect={toggleDocSelection}
                  onDocDelete={deleteDoc}
                  isLoading={isLoading}
                />
              )}

              {viewMode === 'grid' && (
                <GridView
                  docs={docs}
                  selectedDocIds={selectedDocIds}
                  onDocClick={openDoc}
                  onDocContextMenu={handleDocContextMenu}
                  onDocSelect={toggleDocSelection}
                  onDocMove={moveDoc}
                  isLoading={isLoading}
                />
              )}

              {viewMode === 'flow' && (
                <FlowView
                  docs={docs}
                  selectedDocIds={selectedDocIds}
                  hasMore={hasMore}
                  isLoading={isLoading}
                  onDocClick={openDoc}
                  onDocContextMenu={handleDocContextMenu}
                  onDocSelect={toggleDocSelection}
                  onLoadMore={loadMore}
                />
              )}
            </>
          )}
        </div>
      </main>

      {/* Context Menu */}
      {contextMenuDoc && (
        <QuickActionsMenu
          doc={contextMenuDoc}
          position={contextMenuPosition}
          isOpen={isContextMenuOpen}
          onClose={handleCloseContextMenu}
          onAction={handleQuickAction}
        />
      )}
    </div>
  );
};

export default DashboardPage;
